/**
 * 予約日時の入力・表示ヘルパ（純粋関数）。
 * 予約は**日本時間で解釈・表示**する（画面に「日本時間」と書いているため）。端末のタイムゾーンに依存すると
 * 海外設定の端末で 9 時間ずれて予約されていた（Codex デザインレビュー）。日本は夏時間が無いので +09:00 固定。
 */

const pad = (n: number) => String(n).padStart(2, '0')
const JST_OFFSET_MS = 9 * 3600_000

/** Date を日本時間の各成分に分解する（UTC 成分を 9 時間ずらして読む） */
function jstParts(d: Date) {
  const j = new Date(d.getTime() + JST_OFFSET_MS)
  return { y: j.getUTCFullYear(), mo: j.getUTCMonth() + 1, d: j.getUTCDate(), h: j.getUTCHours(), mi: j.getUTCMinutes() }
}

/** Date → 日本時間の 'YYYY-MM-DDTHH:mm'（datetime-local の value / min 形式） */
export function toJstInputValue(d: Date): string {
  const p = jstParts(d)
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}`
}

/**
 * 予約日時の入力（日本時間の 'YYYY-MM-DDTHH:mm'）を検証して ISO 文字列にする。
 * 空・不正・現在以前はエラーメッセージを返す（サーバーへ過去日時を送らない）
 */
export function parseFutureJst(value: string, now: Date = new Date()): { iso: string } | { error: string } {
  if (!value) return { error: '日時を入力してください' }
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return { error: '日時の形式が正しくありません' }
  const d = new Date(`${value}:00+09:00`)
  // 存在しない日付（2/29・4/31 等）は Date が翌月へ繰り上げるので、往復して一致するかで弾く
  if (Number.isNaN(d.getTime()) || toJstInputValue(d) !== value) return { error: '存在しない日時です。日付を確認してください' }
  if (d.getTime() <= now.getTime()) return { error: '過去の日時は指定できません。これから先の日時を選んでください' }
  return { iso: d.toISOString() }
}

/** 予約日時の表示（日本時間。例: 10/12 09:30。今年でなければ 2027/1/5 09:30 — DESIGN-SYSTEM.md の Voice） */
export function formatSchedule(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const p = jstParts(d)
  const year = p.y === jstParts(now).y ? '' : `${p.y}/`
  return `${year}${p.mo}/${p.d} ${pad(p.h)}:${pad(p.mi)}`
}
